import { useCurrentPath } from "@/hooks";
import { PageService } from "@/services/admin-services";
import { Page as AdminPage } from "@/services/admin-services/models";
import { Page as UserPage } from "@/services/user-services/models";
import { useDataStore } from "@/stores";
import { ROOT_NODE, useEditor, useNode } from "@craftjs/core";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, generatePath, useParams } from "react-router-dom";
import { NavigationItem } from "./models/NavigationItem";

const GENERAL_INDENT_PX = 8;
const INDENT_PER_LEVEL_PX = 16;
const INDENT_SHIFT_PX = 1;

const TableOfContents = () => {
    const pages = useDataStore((state) => state.pages);
    const [tocPages, setTocPages] = useState<UserPage[] | AdminPage[]>(pages);
    const [tableOfContents, setTableOfContents] = useState<NavigationItem | null>();
    const { id } = useNode();
    const { enabled, language, order, parent, slug, title, toc, tocParent, type } = useEditor((state) => ({
        enabled: state.options.enabled,
        language: state.nodes[ROOT_NODE].data.props.language,
        order: state.nodes[ROOT_NODE].data.props.parentOrder,
        parent: state.nodes[ROOT_NODE].data.props.parentId,
        slug: state.nodes[ROOT_NODE].data.props.slug,
        title: state.nodes[ROOT_NODE].data.props.title,
        type: state.nodes[ROOT_NODE].data.props.pageType,
        toc: state.nodes[id].data.props.toc,
        tocParent: state.nodes[id].data.props.tocParent,
    }));

    const currentPath = useCurrentPath();
    const params = useParams();
    const { "*": pageSlug, identifier } = params;

    useEffect(() => {
        if (!enabled) return;

        const pageService = new PageService("int");
        pageService.getPages().then((pages) => {
            setTocPages(pages.filter((x) => x.language === language && x.published));
        });
    }, [enabled, language]);

    useEffect(() => {
        setTocPages(pages);
    },[pages])

    useEffect(() => {
        if (!tocPages || tocPages.length === 0) return;

        const currentPage = tocPages.find((page) => page.slug === slug);

        if (parent && currentPage?.id !== tocParent) {
            let parentId = parent;
            let parentPage;

            while (parentId !== undefined) {
                parentPage = tocPages.find((page) => page.id === parentId);
                if (tocParent && parentId === tocParent) break;

                parentId = parentPage?.parentPageId;
            }

            if (parentPage) {
                const navigation = buildNavigationItems(parentPage);
                setTableOfContents(navigation);
                return;
            }
        }

        if (currentPage) {
            const navigation = buildNavigationItems(currentPage);
            setTableOfContents(navigation);
            return;
        }

        setTableOfContents(null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parent, order, title, toc, slug, tocParent, tocPages]);

    const buildNavigationItems = (page: AdminPage | UserPage) => {
        const navigationItem: NavigationItem = {
            id: page.id,
            title: page.title,
            to: (page as UserPage).url || page.slug,
            type: page.pageType,
            order: page.parentOrder,
            isCurrent:
                page.slug === slug ||
                page.id === identifier ||
                (Boolean(pageSlug) && (page as UserPage).url === pageSlug),
            isPublished: page.published,
            children: [],
        };

        let filter = (childPage: AdminPage | UserPage) => childPage.parentPageId === page.id;
        if (enabled)
            filter = (childPage: AdminPage | UserPage) =>
                childPage.parentPageId === page.id && childPage.id !== identifier && childPage.id !== "new-page";
        const children = tocPages.filter(filter);

        if (enabled && parent === page.id && page.id !== identifier) {
            children.push({
                ...page,
                id: identifier || "new-page",
                slug,
                title,
                pageType: type,
                parentOrder: order,
                parentPageId: parent,
                url: "",
                comments: [],
                created: page.created ? new Date(page.created) : new Date(),
                modified: page.modified ? new Date(page.modified) : new Date(),
            });
        }

        if (children.length > 0) {
            navigationItem.children = [];
            children
                .sort((a, b) => a.parentOrder - b.parentOrder)
                .forEach((childPage) => {
                    navigationItem.children?.push(buildNavigationItems(childPage));
                });
        }

        return navigationItem;
    };

    const renderNavigation = (item: NavigationItem, level: number = 0): React.ReactNode => {
        const isActive = item.isCurrent;

        return (
            <div key={item.id} className={clsx([level > 0 && `ml-4`])}>
                <Link
                    to={generatePath(currentPath, { ...params, "*": item.to }).replace(":/", "")}
                    className={clsx(
                        "relative block py-1 px-2 text-sm text-zinc-800",
                        isActive && "font-semibold",
                        level === 0 && "font-medium italic"
                    )}
                >
                    {isActive && (
                        <>
                            <span
                                className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] bg-primary-500 z-10"
                                style={{
                                    transform: `translateX(-${GENERAL_INDENT_PX + level * INDENT_PER_LEVEL_PX + INDENT_SHIFT_PX}px)`,
                                }}
                                aria-hidden="true"
                            />
                            <span
                                className="pointer-events-none absolute left-0 top-0 bottom-0 right-0 bg-primary-500/10 z-10 w-[240px]"
                                style={{
                                    transform: `translateX(-${GENERAL_INDENT_PX + level * INDENT_PER_LEVEL_PX + INDENT_SHIFT_PX}px)`,
                                }}
                                aria-hidden="true"
                            />
                        </>
                    )}
                    {item.title} {!item.isPublished && <span className="text-xs text-gray-500 italic"> (Draft)</span>}
                </Link>

                {item.children?.length ? item.children.map((child) => renderNavigation(child, level + 1)) : null}
            </div>
        );
    };

    return (
        <div
            className={clsx(
                "relative max-w-[250px] px-2 w-[250px]",
                "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-gray-300"
            )}
        >
            {tableOfContents && renderNavigation(tableOfContents)}
        </div>
    );
};

export default TableOfContents;
